/**
 * Firestore Repo Layer
 * --------------------
 * Centralizes story progress reads/writes so UI doesn't touch Firestore.
 */

import { doc, getDoc, getDocs, setDoc, deleteDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { getStorage, ref, getBytes } from "firebase/storage";
import { getDownloadURL } from "firebase/storage";
import { db } from "../firebase";

export async function getStoryProgress({ uid, language, storyId }) {
  if (!uid) throw new Error("getStoryProgress: uid is required");
  if (!language) throw new Error("getStoryProgress: language is required");
  if (!storyId) throw new Error("getStoryProgress: storyId is required");

  const ref = doc(db, "users", uid, `storyProgress_${language}`, storyId);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

/**
 * Ensures a progress doc exists and updates lastReadAt.
 * Returns the (possibly existing) progress data after ensuring.
 */
export async function ensureStoryProgressOnOpen({ uid, language, storyId, title }) {
  const ref = doc(db, "users", uid, `storyProgress_${language}`, storyId);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    // Update lastReadAt
    await updateDoc(ref, { lastReadAt: serverTimestamp() });
    return snap.data();
  }

  // Create new progress doc
  await setDoc(ref, {
    title: title ?? null,
    status: "in_progress",
    startedAt: serverTimestamp(),
    lastReadAt: serverTimestamp(),
  });

  return null;
}

export async function markStoryFinished({ uid, language, storyId }) {
  const ref = doc(db, "users", uid, `storyProgress_${language}`, storyId);
  await updateDoc(ref, {
    status: "finished",
    finishedAt: serverTimestamp(),
    lastReadAt: serverTimestamp(),
  });
}

export async function markStoryInProgress({ uid, language, storyId }) {
  const ref = doc(db, "users", uid, `storyProgress_${language}`, storyId);
  await updateDoc(ref, {
    status: "in_progress",
    finishedAt: null,
    lastReadAt: serverTimestamp(),
  });
}

export async function isStoryFavorite({ uid, language, storyId }) {
    if (!uid || !storyId) return false;
  
    const ref = doc(db, "users", uid, `favoriteStories_${language}`, storyId);
    const snap = await getDoc(ref);
    return snap.exists();
  }
  
  export async function addFavoriteStory({ uid, language, story }) {
    const ref = doc(db, "users", uid, `favoriteStories_${language}`, story.id);
  
    await setDoc(ref, {
      storyId: story.id,
      title: story.title,
      level: story.level ?? null,
      favoritedAt: serverTimestamp(),
    });
  }
  
  export async function removeFavoriteStory({ uid, language, storyId }) {
    const ref = doc(db, "users", uid, `favoriteStories_${language}`, storyId);
    await deleteDoc(ref);
  }

  export function subscribeToContinueStories({ uid, language, onChange, onError }) {
    if (!uid) throw new Error("subscribeInProgressStoryProgress: uid is required");
    if (!language) throw new Error("subscribeInProgressStoryProgress: language is required");
    if (typeof onChange !== "function") throw new Error("subscribeInProgressStoryProgress: onChange must be a function");
  
    const col = collection(db, "users", uid, `storyProgress_${language}`);

    const qIndexed = query(
      col,
      where("status", "==", "in_progress"),
      orderBy("lastReadAt", "desc")
    );
  
    return onSnapshot(
      qIndexed,
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        onChange(docs);
      },
      async (err) => {
        // fallback: no orderBy (does not need composite index usually)
        if (err?.code === "failed-precondition") {
          console.warn("Missing index; falling back to unordered query.");
          const qFallback = query(col, where("status", "==", "in_progress"));
  
          return onSnapshot(
            qFallback,
            (snapshot) => {
              const docs = snapshot.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                // sort locally as a fallback
                .sort((a, b) => (b.lastReadAt?.seconds || 0) - (a.lastReadAt?.seconds || 0));
  
              onChange(docs);
            },
            (e2) => onError?.(e2)
          );
        }
  
        onError?.(err);
      }
    );
  }

  /**
 * Subscribe to favorite story IDs for a user.
 * Path: users/{uid}/favoriteStories_{language}
 * Calls onChange with array of storyIds.
 */
export function subscribeFavoriteStoryIds({ uid, language, onChange, onError }) {
    if (!uid) throw new Error("subscribeFavoriteStoryIds: uid is required");
    if (!language) throw new Error("subscribeFavoriteStoryIds: language is required");
    if (typeof onChange !== "function") throw new Error("subscribeFavoriteStoryIds: onChange must be a function");
  
    const ref = collection(db, "users", uid, `favoriteStories_${language}`);
  
    return onSnapshot(
      ref,
      (snapshot) => {
        const ids = snapshot.docs
          .map((d) => d.data()?.storyId)
          .filter(Boolean);
        onChange(ids);
      },
      (err) => onError?.(err)
    );
  }

  /**
 * Subscribe to finished story IDs for a user.
 * Path: users/{uid}/storyProgress_{language} where status == "finished"
 * Calls onChange with array of storyIds (doc ids).
 */
export function subscribeFinishedStoryIds({ uid, language, onChange, onError }) {
    if (!uid) throw new Error("subscribeFinishedStoryIds: uid is required");
    if (!language) throw new Error("subscribeFinishedStoryIds: language is required");
    if (typeof onChange !== "function") throw new Error("subscribeFinishedStoryIds: onChange must be a function");
  
    const q = query(
      collection(db, "users", uid, `storyProgress_${language}`),
      where("status", "==", "finished")
    );
  
    return onSnapshot(
      q,
      (snapshot) => {
        const ids = snapshot.docs.map((d) => d.id);
        onChange(ids);
      },
      (err) => onError?.(err)
    );
  }

// Called by Home.js
export async function fetchAllStoryCards(language) {
  const snapshot = await getDocs(collection(db, "stories"));
  return snapshot.docs
    .filter((d) => d.data().availableLanguages?.includes(language))
    .map((d) => {
      const data = d.data();
      const lang = data[`languages.${language}`];
      return {
        id: d.id,
        title: lang?.title,
        level: lang?.level,
        series: Array.isArray(data.series) ? data.series : [data.series].filter(Boolean),
        genres: Array.isArray(lang?.genres) ? lang.genres : [lang?.genre || "outros"],
        top10: data.top10 ?? null,
        cardImage: lang?.cardImageUrl,
        coverImage: lang?.coverImageUrl,
        numSentences: lang?.numSentences,
        estimatedMinutes: lang?.estimatedMinutes,
        access: data.access || "paid",
      };
    });
}


const storage = getStorage();

async function fetchStorageJson(path) {
  const encodedPath = encodeURIComponent(path).replace(/%2F/g, '%2F');
  const url = `https://firebasestorage.googleapis.com/v0/b/private-guide-af0ae.firebasestorage.app/o/${encodedPath}?alt=media`;
  const res = await fetch(url);
  return res.json();
}

// Called by StoryReader.js
export async function fetchStoryAssets(storyId, language) {
  const docSnap = await getDoc(doc(db, "stories", storyId));
  if (!docSnap.exists()) throw new Error("Story not found");

  const data = docSnap.data();
  const lang = data[`languages.${language}`];
  if (!lang) throw new Error(`No data for language: ${language}`);

  const storagePath = `stories/${storyId}/${language}`;

  const [storyData, tokensData] = await Promise.all([
    fetchStorageJson(`${storagePath}/story.json`),
    fetchStorageJson(`${storagePath}/tokens.json`).catch(() => null),
  ]);

  const encodedAudio = encodeURIComponent(`${storagePath}/audio.mp3`).replace(/%2F/g, '%2F');
  storyData.audio = `https://firebasestorage.googleapis.com/v0/b/private-guide-af0ae.firebasestorage.app/o/${encodedAudio}?alt=media`;

  return {
    storyData,
    tokensData,
    quizUrl: `${storagePath}/quiz.json`,  // pass path, not URL
    access: data.access ?? "paid",
  };
}
